import ActionDisabledTooltip from '@/components/buttons/ActionDisabledTooltip';
import { TransferBulkApprovals } from '@/components/buttons/TransferBulk/TransferBulkApprovals';
import { TransferBulkError } from '@/components/buttons/TransferBulk/TransferBulkError';
import { TransferBulkOverview } from '@/components/buttons/TransferBulk/TransferBulkOverview';
import { TransferBulkSend } from '@/components/buttons/TransferBulk/TransferBulkSend';
import { TransferBulkSuccess } from '@/components/buttons/TransferBulk/TransferBulkSuccess';
import { useTransferBulk } from '@/components/buttons/TransferBulk/useTransferBulk';
import { useTokenSelection } from '@/components/features/Account/AccountNFTList/useTokenSelection';
import { TokenBeamer } from '@/constants/abis/TokenBeamer';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { useAuthentication } from '@/hooks/useAuthentication';
import { ReservoirAction } from '@/utils/reservoir';
import { getUniqueValues } from '@onbeam/utils';
import { Modal } from '@sphere/ui';
import { rem } from 'polished';
import { cloneElement, forwardRef, useState } from 'react';
import { Address } from 'viem';
import { useReadContract } from 'wagmi';
import { TransferBulkProps, TransferState } from './TransferBulk.types';

const transferScreenMap = {
  [TransferState.IDLE]: TransferBulkOverview,
  [TransferState.APPROVE]: TransferBulkApprovals,
  [TransferState.TRANSFER]: TransferBulkSend,
  [TransferState.SUCCESS]: TransferBulkSuccess,
  [TransferState.ERROR]: TransferBulkError,
};

export const TransferBulk = ({
  trigger,
  openState: customOpenState,
  onClose,
}: TransferBulkProps) => {
  const defaultOpenState = useState(false);
  const [isOpen, setOpen] = customOpenState || defaultOpenState;
  const [transferState, setTransferState] = useState(TransferState.IDLE);

  const [selectedTokens, resetTokenSelectionStore] = useTokenSelection(state => [
    state.selectedTokens,
    state.reset,
  ]);
  const marketplaceChain = useMarketplaceChain();
  const { userAddress } = useAuthentication();

  const contracts = selectedTokens.map(token => token.contractAddress) as Address[];

  const { data: contractApprovals, refetch } = useReadContract({
    chainId: marketplaceChain.id,
    account: userAddress!,
    address: marketplaceChain.contracts?.tokenBeamer?.address,
    abi: TokenBeamer,
    functionName: 'getApprovals',
    args: [
      userAddress!,
      marketplaceChain.contracts?.tokenBeamer?.address!,
      contracts,
      selectedTokens.map(token => token.kind),
      [],
      [],
    ],
  });

  const handleClose = () => {
    setOpen(false);
    useTransferBulk.setState({ hash: undefined, recipientAddress: undefined });
    setTransferState(prevState => {
      const wasSuccessful = prevState === TransferState.SUCCESS;
      onClose?.(wasSuccessful);

      if (wasSuccessful) resetTokenSelectionStore();

      return TransferState.IDLE;
    });
  };

  const unapprovedContracts = getUniqueValues(
    contracts.filter((_, index) => !contractApprovals?.[index]),
  );

  const Trigger = forwardRef((props, ref) =>
    cloneElement(trigger, {
      ref,
      ...props,
      onClick: () => setOpen(true),
    }),
  );

  const Screen = transferScreenMap[transferState];

  return (
    <>
      <ActionDisabledTooltip action={ReservoirAction.Transfer}>
        <Trigger />
      </ActionDisabledTooltip>
      <Modal testId="transfer-bulk-modal" isOpen={isOpen} onClose={handleClose} maxW={rem(512)}>
        <Screen
          unapprovedContracts={unapprovedContracts}
          setTransferState={transferState => {
            setTransferState(transferState);

            /* Refetch the contract approvals when the user navigates back to the overview/idle view */
            if (transferState === TransferState.IDLE) {
              refetch();
            }
          }}
          onClose={handleClose}
        />
      </Modal>
    </>
  );
};
